<template>
  <v-container fluid grid-list-lg>
    <v-layout row wrap mb-2 class="grey lighten-4">
      <h2>
        <router-link to="/terminals">Terminals</router-link>
        <span class="grey--text">&gt;</span>
        Add
      </h2>
    </v-layout>

    <v-form v-model="isFormValid" ref="form" @submit.prevent>

      <!-- Core Fields -->
      <v-layout row wrap align-center>
        <h3 class="pa-2 primary--text">Terminal Details</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Name *"
            v-model="fields.name"
            box
            required
            :rules="[ v => !!v || 'Name is required' ]"
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-layout row align-center>
            <v-flex>
              <v-text-field
                label="Code *"
                v-model="fields.code"
                box
                required
                :rules="[
                  v => !!v || 'Code is required',
                  v => (v && v.length >= 3 && v.length <= 12) || 'Code must be 3-12 characters'
                ]"
                hint="3-12 characters, will be uppercased"
                persistent-hint
              ></v-text-field>
            </v-flex>
            <v-flex shrink class="pl-2 pb-3">
              <v-btn small outline color="primary" :loading="generatingCode" @click="generateCode">Generate</v-btn>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-autocomplete
            label="Account *"
            v-model="fields.account_id"
            :items="myProfile.accounts"
            item-text="name"
            item-value="account_id"
            box
            required
            :rules="[ v => !!v || 'Account is required' ]"
            @change="onAccountChange"
          ></v-autocomplete>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-select
            label="Type"
            v-model="fields.type"
            :items="['retail', 'ecom', 'moto', 'ach']"
            box
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-select>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-select
            label="API"
            v-model="fields.api"
            :items="apiOptions"
            box
          ></v-select>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-select
            label="Processor"
            v-model="fields.processor_id"
            :items="processors"
            item-text="name"
            item-value="id"
            box
            clearable
            :no-data-text="fields.account_id ? 'No processors found for this account' : 'Select an account first'"
          ></v-select>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-select
            label="Processor Account"
            v-model="fields.processor_account_id"
            :items="processorAccounts"
            item-text="name"
            item-value="id"
            box
            clearable
            :loading="loadingProcessorAccounts"
            no-data-text="No processor accounts found"
          >
            <template slot="item" slot-scope="{ item }">
              <v-list-tile-content>
                <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                <v-list-tile-sub-title v-if="item.type || item.mid">
                  {{ [item.type, item.mid].filter(Boolean).join(' - ') }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-select>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Key"
            v-model="fields.key"
            box
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Password"
            v-model="fields.password"
            box
          ></v-text-field>
        </v-flex>

        <v-flex xs12 sm6 lg4>
          <v-text-field
            label="Serial Number"
            v-model="fields.serial_number"
            box
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 sm3>
          <v-checkbox
            label="Active"
            v-model="fields.active"
            color="primary"
          ></v-checkbox>
        </v-flex>
        <v-flex xs12 sm3>
          <v-checkbox
            label="Testing"
            v-model="fields.is_testing"
            color="primary"
          ></v-checkbox>
        </v-flex>
        <v-flex xs12 sm3>
          <v-checkbox
            label="Virtual"
            v-model="fields.is_virtual"
            color="primary"
          ></v-checkbox>
        </v-flex>
        <v-flex xs12 sm3>
          <v-checkbox
            label="Split"
            v-model="fields.is_split"
            color="primary"
          ></v-checkbox>
        </v-flex>
      </v-layout>

      <!-- Address Section -->
      <v-layout row wrap align-center>
        <h3 class="pa-2 primary--text">Address</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12 sm6>
          <v-text-field
            label="Address"
            v-model="fields.address"
            box
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg3>
          <v-text-field
            label="City"
            v-model="fields.city"
            box
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg3>
          <StateDropdown v-model="fields.state" />
        </v-flex>
        <v-flex xs12 sm6 lg3>
          <v-text-field
            label="Postal Code"
            v-model="fields.postal_code"
            box
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm6 lg3>
          <CountryDropdown v-model="fields.country" format="text" />
        </v-flex>
        <v-flex xs12 sm6 lg3>
          <v-text-field
            label="Phone"
            v-model="fields.phone"
            box
          ></v-text-field>
        </v-flex>
      </v-layout>

      <!-- Users Section -->
      <v-layout row wrap align-center>
        <h3 class="pa-2 primary--text">Users</h3>
        <v-divider class="primary"></v-divider>
      </v-layout>

      <v-layout row wrap>
        <v-flex xs12>
          <p class="caption grey--text mb-2">
            Select users from the account to link to this terminal.
            <span v-if="!fields.account_id">Select an account first.</span>
          </p>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="fields.account_id">
        <v-flex xs12>
          <v-data-table
            :headers="userHeaders"
            :items="accountUsers"
            :loading="loadingAccountUsers"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <td>{{ props.item.id }}</td>
              <td>{{ props.item.name }}</td>
              <td>{{ props.item.email }}</td>
              <td>{{ props.item.position }}</td>
              <td>
                <v-checkbox
                  v-model="selectedUserIds"
                  :value="props.item.id"
                  color="primary"
                  hide-details
                  class="mt-0"
                ></v-checkbox>
              </td>
              <td>
                <v-checkbox
                  v-model="userCanCharge[props.item.id]"
                  color="primary"
                  hide-details
                  class="mt-0"
                  :disabled="!selectedUserIds.includes(props.item.id)"
                ></v-checkbox>
              </td>
            </template>
            <template v-slot:no-data>
              <v-alert :value="true" type="info">
                No users found for this account
              </v-alert>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>

      <v-divider class="primary mt-4"></v-divider>

      <v-layout row wrap justify-end class="mt-3">
        <v-btn color="primary" outline :to="{ name: 'Terminals' }">Cancel</v-btn>
        <v-btn color="primary" @click.stop="save" :disabled="!isFormValid" :loading="isProcessing">Create</v-btn>
      </v-layout>
    </v-form>

  </v-container>
</template>

<script>
import Bus from '@/bus'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import StateDropdown from '@/components/forms/StateDropdown'
import CountryDropdown from '@/components/forms/CountryDropdown'

export default {
  components: { StateDropdown, CountryDropdown },
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin],

  data: function () {
    return {
      isFormValid: false,
      isProcessing: false,
      generatingCode: false,

      fields: this.defaultFormFields(),

      processors: [],
      apiOptions: ['jetpay', 'paynetworx', 'paymentspring', 'trxservices', 'fpg'],

      processorAccounts: [],
      loadingProcessorAccounts: false,


      accountUsers: [],
      loadingAccountUsers: false,
      selectedUserIds: [],
      userCanCharge: {},

      userHeaders: [
        { text: 'ID', value: 'id', sortable: false },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Position', value: 'position', sortable: false },
        { text: 'Link', value: 'link', sortable: false },
        { text: 'Can Charge', value: 'can_charge', sortable: false }
      ]
    }
  },

  mounted () {
    if (this.$route.query.account_id) {
      const accountId = parseInt(this.$route.query.account_id)
      this.fields.account_id = accountId
      this.onAccountChange(accountId)
    }
  },

  watch: {
    'fields.processor_id' () { this.loadProcessorAccounts() }
  },

  methods: {
    defaultFormFields () {
      return {
        name: '',
        code: '',
        account_id: null,
        processor_account_id: null,
        type: [],
        api: 'jetpay',
        processor_id: null,
        key: '',
        password: '',
        serial_number: '',
        active: true,
        is_testing: false,
        is_virtual: false,
        is_split: false,
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: ''
      }
    },

    async loadProcessorAccounts () {
      this.processorAccounts = []
      this.fields.processor_account_id = null

      if (!this.fields.processor_id) return
      if (!this.fields.account_id) return

      this.loadingProcessorAccounts = true
      try {
        let result = await this.api({
          url: '/terminals/processor-accounts',
          params: {
            processor_id: this.fields.processor_id,
            account_id: this.fields.account_id
          }
        })
        this.processorAccounts = result.data.accounts
      } catch (error) {
        console.error('Error loading processor accounts:', error)
      } finally {
        this.loadingProcessorAccounts = false
      }
    },

    async generateCode () {
      this.generatingCode = true
      try {
        let result = await this.api({ url: '/terminals/generate-code' })
        this.fields.code = result.data.code
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: 'Failed to generate code' })
      } finally {
        this.generatingCode = false
      }
    },

    async loadAccountProcessors (accountId) {
      this.processors = []
      this.fields.processor_id = null
      this.processorAccounts = []
      this.fields.processor_account_id = null

      if (!accountId) return

      try {
        let result = await this.api({
          url: '/terminals/account-processors',
          params: { account_id: accountId }
        })
        this.processors = result.data.processors
      } catch (error) {
        console.error('Error loading account processors:', error)
      }
    },

    async onAccountChange (accountId) {
      this.selectedUserIds = []
      this.userCanCharge = {}
      if (accountId) {
        await Promise.all([
          this.loadAccountUsers(accountId),
          this.loadAccountProcessors(accountId)
        ])
      } else {
        this.accountUsers = []
        this.processors = []
        this.processorAccounts = []
        this.fields.processor_id = null
        this.fields.processor_account_id = null
      }
    },

    async loadAccountUsers (accountId) {
      this.loadingAccountUsers = true
      try {
        let result = await this.api({
          method: 'get',
          url: '/users',
          params: {
            filter: { account_id: accountId },
            perPage: '*'
          }
        })
        this.accountUsers = result.data.rows
      } catch (error) {
        console.error('Error loading account users:', error)
        this.accountUsers = []
      } finally {
        this.loadingAccountUsers = false
      }
    },

    async save () {
      if (!this.$refs.form.validate()) {
        let errors = []
        this.$refs.form.inputs.forEach(e => {
          if (e.hasError) errors.push(e.errorBucket[0])
        })
        Bus.$emit('openModal', {
          status: 'error',
          message: errors.length > 1 ? 'Validation Errors' : 'Validation Error',
          messageDetails: errors
        })
        return false
      }

      this.isProcessing = true
      try {
        const { processor_account_id, ...terminalFields } = this.fields
        terminalFields.type = Array.isArray(terminalFields.type) ? terminalFields.type.join('/') : terminalFields.type
        await this.api({
          method: 'post',
          url: '/terminals/create',
          data: {
            terminal: terminalFields,
            user_ids: this.selectedUserIds,
            user_can_charge: this.userCanCharge,
            processor_account_id: processor_account_id || null
          }
        })

        Bus.$emit('openModal', { status: 'success', message: 'Terminal created' })
        this.$router.push({ name: 'Terminals' })
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/manage/TerminalEdit.vue